import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchParam } from '@common/ts/enums';
import { IconLoader2 } from '@tabler/icons-react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { ConversationState, EProjectStatus, QuestionsTab } from '@/enums';
import useTranslation from '@/hooks/useTranslation';
import {
  translateDivergentViewsAndOutliers,
  translateKeyThemes,
  translateNoKeyThemesData,
  translateNoOpportunitiesData,
  translateNoSummaryData,
  translateOpportunities,
  translatePatternsAndTrends,
  translateSummary,
} from '@/pages/projects/single-project/Content/project_results/resultsPageTranslations';
import PaperAirplaneIcon from '../icons/PaperAirplaneIcon';
import { Button } from '../ui/button';
import Categories from './Categories';
import ProjectSummary from '../projects/single-project/project-summary/ProjectSummary';
import { InsightsSubset, ProjectProps, QuestionTabs, SelectedCategory } from '@/types';
import { Transcripts } from './Transcripts';
import ProjectQuestionInsights from './ProjectQuestionInsights';
import QuestionItemTabs from './QuestionItemTabs';
import { resolveTranslation } from '@/utils/resolveTranslation';
import { useGetQuestionCategories } from '@/reactQuery/get';

interface QuestionItemProps {
  isPendingReRun: boolean;
  selectedQuestionInsights?: InsightsSubset;
  questionID: string;
  questionText: string;
  analysisLanguage: string;
  singleProjectData?: ProjectProps;
  newConversationsCount: number;
  onReRun: () => void;
  isRerunLoading: boolean;
  newConversationsState: ConversationState;
  projectId?: string;
  isPendingInterviewQuestions: boolean;
}

function QuestionItem({
  isPendingReRun,
  selectedQuestionInsights,
  questionID,
  questionText,
  analysisLanguage,
  singleProjectData,
  newConversationsCount,
  onReRun,
  isRerunLoading,
  newConversationsState,
  projectId,
  isPendingInterviewQuestions,
}: QuestionItemProps) {
  const { lang } = useTranslation();
  const { currentTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const rerunFilter = localStorage.getItem(`rerun_filter_${filterId}`);

  const [activeTab, setActiveTab] = useState<QuestionsTab>(QuestionsTab.INSIGHTS);
  const [selectedCategories, setSelectedCategories] = useState<SelectedCategory[]>([]);
  const handleTabFocus = (tab: QuestionsTab) => setActiveTab(tab);
  const tabStyle = (tab: QuestionsTab) => ({
    backgroundColor: activeTab === tab ? currentTheme?.primary : 'transparent',
    color: activeTab === tab ? 'white' : 'black',
  });

  const noEnoughInterviewData =
    selectedQuestionInsights?.summary?.length === 0 &&
    selectedQuestionInsights?.themes?.length === 0 &&
    selectedQuestionInsights?.quotes?.length === 0 &&
    selectedQuestionInsights?.opportunities?.length === 0;
  const keyThemesTitle = selectedQuestionInsights?.patterns_and_trends
    ? resolveTranslation(translatePatternsAndTrends, analysisLanguage)
    : resolveTranslation(translateKeyThemes, analysisLanguage);
  const opportunitiesTitle = selectedQuestionInsights?.divergent_views_and_outliers
    ? resolveTranslation(translateDivergentViewsAndOutliers, analysisLanguage)
    : resolveTranslation(translateOpportunities, analysisLanguage);
  const isCompletedWithInsufficientDataAndFilter =
    singleProjectData?.status === EProjectStatus.COMPLETED && noEnoughInterviewData && !!filterId;
  const isReRunInProgress = isPendingReRun || isRerunLoading;
  const dataWithStepsIsLoading = isPendingReRun || isPendingInterviewQuestions;
  const isApplyingFilter = (rerunFilter || isRerunLoading) && Boolean(filterId);
  const isRunning = (!filterId || (filterId && isRerunLoading)) && !rerunFilter;
  let messageKey = '';
  if (isApplyingFilter) {
    messageKey = 'msg.applyingFilter';
  } else if (isRunning) {
    messageKey = 'msg.running';
  }

  const { data: categories, isPending: isPendingCategories } = useGetQuestionCategories(
    filterId,
    projectId,
    questionID,
  );

  useEffect(() => {
    if (
      !newConversationsState ||
      !selectedQuestionInsights ||
      noEnoughInterviewData ||
      newConversationsState === 'done'
    )
      return;
    if (rerunFilter && newConversationsState === 'idle') {
      localStorage.removeItem(`rerun_filter_${filterId}`);
    }
  }, [
    rerunFilter,
    filterId,
    newConversationsState,
    selectedQuestionInsights,
    noEnoughInterviewData,
  ]);
  if ((noEnoughInterviewData || rerunFilter) && isRerunLoading) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full min-h-[50vh] text-center">
        <Button
          variant="default"
          disabled
          className="flex items-center gap-2 mr-[4%] font-bold px-7"
        >
          <IconLoader2 className="animate-spin" size={20} />
          <p className="epilogue-font">{lang.get(messageKey)}</p>
        </Button>
      </div>
    );
  }

  const tabs: QuestionTabs[] = [
    {
      value: QuestionsTab.INSIGHTS,
      labelKey: 'msg.insights',
      disabled: isPendingInterviewQuestions,
      isLoading: false,
    },
    {
      value: QuestionsTab.ANSWERS,
      labelKey: 'msg.answers',
      disabled: isPendingInterviewQuestions || isPendingCategories,
      isLoading: isPendingCategories,
    },
  ];

  const onSelectCategoryHandler = (selectedCategory: SelectedCategory) => {
    setActiveTab(QuestionsTab.ANSWERS);
    setSelectedCategories([selectedCategory]);
  };

  return isCompletedWithInsufficientDataAndFilter ? (
    <div className="flex flex-col items-center justify-center w-full h-full min-h-[50vh] text-center">
      <p className="font-medium">{lang.get('msg.noInterviewsThatMatchThisFilter')}</p>
    </div>
  ) : (
    <Tabs
      value={activeTab}
      onValueChange={(value) => setActiveTab(value as QuestionsTab)}
      defaultValue={QuestionsTab.INSIGHTS}
      className="w-full"
    >
      {/* TABS LIST */}
      <QuestionItemTabs
        tabs={tabs}
        handleTabFocus={handleTabFocus}
        tabStyle={tabStyle}
        lang={lang}
      />

      {/* INSIGHTS CONTENT  */}
      <TabsContent value="insights" className="pb-10">
        {!noEnoughInterviewData && !rerunFilter && activeTab === QuestionsTab.INSIGHTS ? (
          <div className="flex flex-col w-full gap-10 py-4 pl-1">
            {/* categories */}
            <Categories
              projectId={projectId}
              questionID={questionID}
              questionText={questionText}
              analysisLanguage={analysisLanguage}
              onSelectCategory={onSelectCategoryHandler}
              categories={categories}
              isPendingCategories={isPendingCategories}
              isPendingInterviewQuestions={isPendingInterviewQuestions}
            />
            {/* summary */}
            <ProjectSummary
              noSummarydata={resolveTranslation(translateNoSummaryData, analysisLanguage)}
              isLoading={isPendingReRun || isPendingInterviewQuestions}
              marginBottom="!mb-4"
              title={resolveTranslation(translateSummary, analysisLanguage)}
              summary={selectedQuestionInsights?.summary ?? ''}
            />

            {/* Key Themes Section */}
            <ProjectQuestionInsights
              data={
                selectedQuestionInsights?.patterns_and_trends ?? selectedQuestionInsights?.themes
              }
              title={keyThemesTitle}
              isLoading={dataWithStepsIsLoading}
              noDataText={resolveTranslation(translateNoKeyThemesData, analysisLanguage)}
            />

            {/* Opportunities Section */}
            <ProjectQuestionInsights
              data={
                selectedQuestionInsights?.divergent_views_and_outliers ??
                selectedQuestionInsights?.opportunities
              }
              title={opportunitiesTitle}
              isLoading={dataWithStepsIsLoading}
              noDataText={resolveTranslation(translateNoOpportunitiesData, analysisLanguage)}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center w-full h-full min-h-[65vh] text-center">
            {singleProjectData?.status !== EProjectStatus.COMPLETED && (
              <Button
                variant="default"
                disabled={isReRunInProgress}
                className="relative flex items-center gap-2 mt-5 font-bold px-7"
                onClick={() => onReRun()}
              >
                {isReRunInProgress ? (
                  <IconLoader2 className="animate-spin" size={20} />
                ) : (
                  <PaperAirplaneIcon className="size-4" />
                )}
                {isReRunInProgress ? 'Running...' : 'Run'}
                {!isRerunLoading && newConversationsCount > 0 && (
                  <span className="absolute flex items-center justify-center h-5 px-1.5 text-xs text-white bg-red-600 rounded-full w-fit -top-1 -right-1.5">
                    {newConversationsCount}
                  </span>
                )}
              </Button>
            )}
          </div>
        )}
      </TabsContent>

      {/* TRANSCRIPTS CONTENT */}
      <TabsContent value="answers">
        {activeTab === QuestionsTab.ANSWERS && (
          <Transcripts
            questionID={questionID}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            categories={categories}
            isPendingInterviewQuestions={isPendingInterviewQuestions}
          />
        )}
      </TabsContent>
    </Tabs>
  );
}

export default QuestionItem;
